import { apiRequest } from '@/domains/api';
import { isObject } from './rankings-api';

/**
 * survey-api's team dashboard (SR-485, `GET /api/v1/admin/dashboard`) — the sole reader: Home's
 * one request, the same for every viewer but `mine`. Every count is survey-api's; nothing here is
 * re-counted. A 2xx of another shape is a failed read.
 */

export interface IDashboardMilestone {
  key: string;
  label: string;
  /** `yyyy-MM-dd`, none while the cycle has not set it. */
  date: Nullable<string>;
}

export interface IDashboardCycle {
  code: string;
  name: string;
  milestones: IDashboardMilestone[];
  next: Nullable<{ label: string; date: string; daysLeft: number }>;
}

/** One survey's funnel, stage by stage. */
export interface IDashboardPipeline {
  survey: string;
  registered: number;
  started: number;
  submitted: number;
  reviewed: number;
  eligible: number;
  ddDone: number;
  figuresReceived: number;
}

export interface IDashboardQueues {
  compliance: { open: number; overdue: number; unassigned: number };
  ddCalls: { total: number; unassigned: number; overdue: number };
  reinstatement: { total: number; editedThisCycle: number };
  metrics: { owed: number; notAsked: number; askedNoReply: number };
  /** The open number checks (SR-487, PFR §4.4): all, those nobody is assigned, and each
   *  reason's — every reason named, zeros included. Absent from an API before SR-487. */
  identityChecks?: { open: number; unassigned: number; byReason: Record<string, number> };
}

/** The last 14 days, oldest first: `days` the `yyyy-MM-dd` each count is of. */
export interface IDashboardActivity {
  days: string[];
  submissions: number[];
  advisorEdits: number[];
  complianceRaised: number[];
  complianceReviewed: number[];
}

/** One person's open work; `accountId` null is the unassigned row. */
export interface IDashboardWorkload {
  accountId: Nullable<string>;
  name: string;
  ddCalls: number;
  ddOverdue: number;
  compliance: number;
  complianceOverdue: number;
  reinstatement: number;
  metrics: number;
  /** The assignee's open number checks (SR-487). Absent from an API before SR-487. */
  identityChecks?: number;
}

export interface IDashboardFreshness {
  brokerCheck: Nullable<{ lastRunAt: string; status: string }>;
  lastImport: Nullable<{ at: string; status: string }>;
}

export interface IDashboardRanking {
  rankingId: string;
  name: string;
  year: number;
  status: string;
  publishOn: Nullable<string>;
}

export interface IDashboard {
  cycle: Nullable<IDashboardCycle>;
  pipeline: IDashboardPipeline[];
  queues: IDashboardQueues;
  activity: IDashboardActivity;
  workload: IDashboardWorkload[];
  freshness: IDashboardFreshness;
  rankings: IDashboardRanking[];
  mine: { tasks: number; overdue: number };
}

export const homeApi = {
  async dashboard(signal?: AbortSignal): Promise<IDashboard> {
    const answer = await apiRequest<unknown>('/api/v1/admin/dashboard', { signal });
    if (
      !isObject(answer) ||
      !Array.isArray(answer.pipeline) ||
      !isObject(answer.queues) ||
      !isObject(answer.activity) ||
      !Array.isArray(answer.workload) ||
      !isObject(answer.freshness) ||
      !Array.isArray(answer.rankings) ||
      !isObject(answer.mine)
    ) {
      throw new Error('The answer held no dashboard');
    }
    return answer as unknown as IDashboard;
  }
};
